import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAdById } from "../../api";
import { useTranslation } from "react-i18next";
import { MdLocationOn, MdPhone, MdDateRange } from "react-icons/md";
import "./AdDetails.css";

const DEFAULT_IMAGE = "/images/default-ad.png";

const AdDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [ad, setAd] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const data = await getAdById(id);
        setAd(data.ad);
        setRelated(data.related || []);
      } catch (err) {
        console.error("Error fetching ad:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [id]);

  // Sticky call button for mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) return <div className="ad-skeleton" />;
  if (!ad) return <div className="not-found">{t("Ad Not Found")}</div>;

  return (
    <main className={`ad-page ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Sticky Header */}
      <header className="ad-header sticky-header">
        <div className="ad-header-bar">
          <button className="back-btn" onClick={() => navigate("/ads")}>
            {isRTL ? "← العودة للإعلانات" : "← Back to Ads"}
          </button>
          <div className="ad-searchbar">
            <input
              className="ad-search-input"
              placeholder={t("Search Ads")}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  navigate(`/ads?q=${encodeURIComponent(e.target.value)}`);
                }
              }}
            />
          </div>
        </div>
      </header>

      {/* Main Ad Card */}
      <div className="ad-wrapper fade-up">
        {/* Image Side */}
        <div className="ad-image-container">
          <div className="ad-image-blur-bg">
            <img
              src={ad.image_url || DEFAULT_IMAGE}
              alt={ad.title}
              className="ad-large-image"
              loading="lazy"
            />
          </div>
        </div>

        {/* Details Side */}
        <div className="ad-info-container">
          <h1 className="ad-title">{ad.title}</h1>
          <div className="ad-price-box">{ad.price}</div>
          <p className="ad-snippet">{ad.snippet}</p>
          <p className="ad-description">{ad.description}</p>

          {/* Info Grid */}
          <div className="info-grid">
            <div className="info-item">
              <MdLocationOn className="info-icon" />
              <span className="info-label">{t("Location")}</span>
              <span className="info-value">{ad.location}</span>
            </div>
            <div className="info-item">
              <MdPhone className="info-icon" />
              <span className="info-label">{t("Phone")}</span>
              <a
                href={`tel:${ad.phone}`}
                className="info-value phone"
                style={{ direction: "ltr", unicodeBidi: "plaintext" }}
              >
                {ad.phone}
              </a>
            </div>
            <div className="info-item">
              <MdDateRange className="info-icon" />
              <span className="info-label">{t("Posted On")}</span>
              <span className="info-value">
                {new Date(ad.start_date).toLocaleDateString(isRTL ? "ar-LB" : "en-US")}
              </span>
            </div>
          </div>

          {/* Call Now Button */}
          {!isMobile && (
            <button
              className="call-action"
              onClick={() => (window.location.href = `tel:${ad.phone}`)}
            >
              <MdPhone style={{ verticalAlign: "middle", marginInlineEnd: 6 }} />
              {t("Call Now")}
            </button>
          )}
        </div>
      </div>

      {/* Related Ads */}
      {related.length > 0 && (
        <section className="related-section fade-up">
          <h2 className="related-title">{t("Related Ads")}</h2>
          <div className="related-scroll">
            {related.map((item) => (
              <div
                key={item.id}
                className="related-card"
                onClick={() => navigate(`/ads/${item.id}`)}
                tabIndex={0}
                role="button"
                aria-label={item.title}
              >
                {/* <img
                  src={item.image_url || DEFAULT_IMAGE}
                  alt={item.title}
                  className="related-img"
                  loading="lazy"
                /> */}
                <div className="related-content">
                  <h4>{item.title}</h4>
                  <p>{item.snippet}</p>
                  <strong>{item.price}</strong>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sticky Call Button for Mobile */}
      {isMobile && (
        <button
          className="call-action sticky-call"
          onClick={() => (window.location.href = `tel:${ad.phone}`)}
        >
          <MdPhone style={{ verticalAlign: "middle", marginInlineEnd: 6 }} />
          {t("Call Now")}
        </button>
      )}
    </main>
  );
};

export default AdDetails;